/**
 * Simple Angular controller that is attached to the GridItems within the index.aspx. The purpose of this is to allow the user to easily
 * update the effective date for multiple items at once.
 */
/* @ngInject */ export function bulkItemsController(
  $scope,
  Alert,
  BooksSvc,
  BusyScreen,
  uiGridConstants
) {
  const alertTitle = 'Item Effective Dates';

  const activeFilter = {
    term: true,
    type: uiGridConstants.filter.SELECT,
    selectOptions: [
      { value: '', label: 'ALL' },
      { value: true, label: 'Y' },
      { value: false, label: 'N' }
    ]
  };

  const COLUMNS = [
    { field: 'category' },
    { field: 'name', cellTooltip: true },
    {
      field: 'effectiveDate',
      cellFilter: 'date:"M/d/yyyy"',
      filterCellFiltered: true,
      width: '150'
    },
    {
      field: 'active',
      displayName: 'Active?',
      width: '90',
      cellFilter: 'boolToString',
      filter: activeFilter
    }
  ];

  const gridOptions = {
    data: 'dbItems',
    enableColumnMenus: false,
    enableHorizontalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
    enableVerticalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
    enableFiltering: true,
    enableRowSelection: true,
    enableSelectAll: true,
    minRowsToShow: 3,
    multiSelect: true,
    enableFullRowSelection: true,
    enableColumnResizing: true,
    showGridFooter: true,
    /**
     * We register the grid api to the $scope so we have reference to which rows are clicked and that way we can interact with them in this file.
     */
    onRegisterApi: function (gridApi) {
      $scope.gridApi = gridApi;
      gridApi.selection.on.rowSelectionChanged($scope, function (row) {
        const selectedRows = gridApi.selection.getSelectedRows();

        $scope.verifySingleSelect(selectedRows.length, row, alertTitle, 1000);
      });
      gridApi.selection.on.rowSelectionChangedBatch($scope, function (rows) {
        const selectedRows = gridApi.selection.getSelectedRows();

        $scope.verifyMultiSelect(selectedRows.length, rows, alertTitle, 1000);
      });
    },
    columnDefs: COLUMNS
  };

  /**
   * Our grid options for the Items are named itemGridOptions, so in order for this to interact with the grid we need to set that within
   * the $scope.
   */
  $scope.itemGridOptions = gridOptions;

  /**
   * Takes the list of selected rows and applies the effective date to them. This is used to pass to the server to update the effective date for
   * those items.
   *
   * @returns void
   */
  function updateItemEffectiveDates() {
    const selectedRows = $scope.gridApi.selection.getSelectedRows();

    if (!selectedRows.length) {
      Alert.info(
        'No items have been selected. Please select one or more items before continuing.',
        alertTitle
      );
      return;
    }

    /**
     * First this verifies that the new effective date is different than the current effective date. If it is not then we do not need to update
     * the effective date.
     *
     * When the user sets the effective date in the input that is set to the props object. We need to take that date and set it on all
     * the selected rows.
     */
    const itemsToUpdate = selectedRows
      /**
       * Verify that the effective dates are not null otherwise it would throw an error.
       *
       * Also when we compare remove the time from the date so that we are only comparing the date and not the time.
       */
      .filter(
        (item) =>
          (item.effectiveDate && item.effectiveDate.toDateString()) !==
          ($scope.props.effectiveDate &&
            $scope.props.effectiveDate.toDateString())
      )
      .map((item) => item.id);

    if (!itemsToUpdate.length) {
      Alert.info(
        'The selected item(s) already have the effective date you are trying to set. Please select a different effective date.',
        alertTitle
      );
      return;
    }

    BusyScreen.setBusy(true);

    BooksSvc.bulkUpdateItemsEffectiveDates(
      itemsToUpdate,
      $scope.props.effectiveDate
    )
      .then(() => BooksSvc.getProductList(null, null, null, null))
      .then((resp) => {
        // Refresh the item data now that we have changed the effective dates.
        $scope.dbItems = $scope
          .filterEffectiveDateItems(resp.data)
          .sortOn('category', 'name');
        // Reset the input to null so the user can select a new effective date.
        $scope.props.effectiveDate = null;
        BusyScreen.setBusy(false);
        Alert.success(
          'The selected item(s) have had their effective dates changed.',
          alertTitle
        );
      })
      .catch((error) => {
        BusyScreen.setBusy(false);

        const errorMesssage =
          error.parsedResponse && error.parsedResponse.message()
            ? error.parsedResponse.message()
            : 'There was an error updating the effective date for those item(s). Please try again.';

        Alert.error(errorMesssage, alertTitle);
      });
  }

  /**
   * Simple function that checks to see if there are any selected rows and verifies an effective date has already been selected by user.
   * If there are then we can save, if not then we cannot save.
   *
   * @returns boolean (true = can Save; false = cannot save)
   */
  function canSaveItems() {
    return $scope.gridApi.selection.getSelectedRows().length > 0;
  }

  /**
   * Attach these to the $scope so they can be used in the index file.
   */
  $scope.canSaveItems = canSaveItems;
  $scope.updateItemEffectiveDates = updateItemEffectiveDates;
}
